/**
 * Standing in for a body, when there isn't one.
 *
 * On a desktop browser there is no compass and usually no useful GPS, but every mode still has
 * to be reachable (`ProductOverview.md` §4.9). WASD and the arrow keys move a simulated walker
 * and turn a simulated head, and the rest of the app cannot tell the difference.
 *
 *   ← →  turn
 *   ↑ ↓  walk forwards and backwards
 *   W A S D  walk forwards, strafe left, backwards, strafe right
 *   Shift  run
 */

import { destinationPoint, normalizeDegrees, type LatLng } from '@beeline/shared';

/** A brisk walk. Shift multiplies it, for crossing a simulated festival field quickly. */
const WALK_METRES_PER_SECOND = 1.5;
const RUN_MULTIPLIER = 6;
const TURN_DEGREES_PER_SECOND = 90;

export interface KeyboardWalkerOptions {
  start: LatLng;
  heading?: number;
  onChange: (state: { position: LatLng; heading: number }) => void;
}

export class KeyboardWalker {
  private readonly held = new Set<string>();
  private position: LatLng;
  private heading: number;
  private frame: number | null = null;
  private lastFrameAt = 0;
  private readonly onChange: KeyboardWalkerOptions['onChange'];

  constructor(options: KeyboardWalkerOptions) {
    this.position = options.start;
    this.heading = options.heading ?? 0;
    this.onChange = options.onChange;
  }

  get state(): { position: LatLng; heading: number } {
    return { position: this.position, heading: this.heading };
  }

  start(): void {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    // A tab switch swallows the keyup, which would leave the walker running forever.
    window.addEventListener('blur', this.onBlur);
  }

  stop(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.held.clear();
    this.stopLoop();
  }

  /** Put the walker somewhere — used when the first real fix arrives. */
  teleport(position: LatLng): void {
    this.position = position;
    this.onChange(this.state);
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    // Never steal keys from a text field: the name input lives on the same screens.
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.isContentEditable)) return;

    const key = normalizeKey(event.key);
    if (!key) return;

    event.preventDefault();
    this.held.add(key);
    this.startLoop();
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    const key = normalizeKey(event.key);
    if (key) this.held.delete(key);
    if (this.held.size === 0) this.stopLoop();
  };

  private readonly onBlur = (): void => {
    this.held.clear();
    this.stopLoop();
  };

  private startLoop(): void {
    if (this.frame !== null) return;
    this.lastFrameAt = performance.now();
    this.frame = window.requestAnimationFrame(this.tick);
  }

  private stopLoop(): void {
    if (this.frame === null) return;
    window.cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  private readonly tick = (now: number): void => {
    const delta = Math.min((now - this.lastFrameAt) / 1000, 0.1);
    this.lastFrameAt = now;

    const speed = WALK_METRES_PER_SECOND * (this.held.has('shift') ? RUN_MULTIPLIER : 1);

    if (this.held.has('left'))
      this.heading = normalizeDegrees(this.heading - TURN_DEGREES_PER_SECOND * delta);
    if (this.held.has('right'))
      this.heading = normalizeDegrees(this.heading + TURN_DEGREES_PER_SECOND * delta);

    // Movement is relative to where you are facing, like a body.
    let bearing: number | null = null;
    if (this.held.has('forward')) bearing = this.heading;
    else if (this.held.has('back')) bearing = this.heading + 180;
    else if (this.held.has('strafeLeft')) bearing = this.heading - 90;
    else if (this.held.has('strafeRight')) bearing = this.heading + 90;

    if (bearing !== null) {
      this.position = destinationPoint(this.position, normalizeDegrees(bearing), speed * delta);
    }

    this.onChange(this.state);

    if (this.held.size > 0) this.frame = window.requestAnimationFrame(this.tick);
    else this.frame = null;
  };
}

function normalizeKey(key: string): string | null {
  switch (key) {
    case 'ArrowLeft':
      return 'left';
    case 'ArrowRight':
      return 'right';
    case 'ArrowUp':
      return 'forward';
    case 'ArrowDown':
      return 'back';
    case 'Shift':
      return 'shift';
    default:
      break;
  }

  switch (key.toLowerCase()) {
    case 'w':
      return 'forward';
    case 's':
      return 'back';
    case 'a':
      return 'strafeLeft';
    case 'd':
      return 'strafeRight';
    case 'q':
      return 'left';
    case 'e':
      return 'right';
    default:
      return null;
  }
}
